/**
 * NotificationItem Component
 *
 * Single notification display with read/unread visual distinction.
 *
 * Features:
 * - Click to mark as read and navigate to action URL
 * - Visual distinction for read/unread status
 * - Relative timestamp display (e.g., "2 hours ago")
 * - Urgency indicator (color-coded)
 *
 * @module components/NotificationItem
 */

import { useNavigate, useLocation } from 'react-router-dom';
import { Notification } from '@/types/notification';
import { useMarkNotificationReadGraphQL } from '@/graphql/hooks/useNotificationsGraphQL';
import logger from '@/utils/logger';
import { cn } from '@/lib/utils';
import { DebugModeOnly } from './DebugModeOnly';
import { AdminAwareIdLink } from './AdminAwareIdLink';

export interface NotificationItemProps {
  notification: Notification;
  onRead?: (notification: Notification) => void;
  onClose?: () => void;
  className?: string;
}

/**
 * Format timestamp as relative time (e.g., "2 hours ago")
 */
function formatRelativeTime(timestamp: string): string {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now.getTime() - then.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  // For older notifications, show date
  return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Get urgency indicator color
 */
function getUrgencyColor(urgency: 'low' | 'medium' | 'high'): string {
  switch (urgency) {
    case 'high':
      return 'bg-danger';
    case 'medium':
      return 'bg-warning';
    case 'low':
      return 'bg-muted';
    default:
      return 'bg-muted';
  }
}

/**
 * NotificationItem - Single notification display
 *
 * Usage:
 *   <NotificationItem
 *     notification={notification}
 *     onRead={handleNotificationRead}
 *   />
 */
export function NotificationItem({
  notification,
  onRead,
  onClose,
  className,
}: NotificationItemProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // GraphQL mutation for marking notifications as read
  const { markRead, loading: markReadLoading } = useMarkNotificationReadGraphQL();

  /**
   * Navigate to action URL with force refresh if already on the same route
   * This ensures that navigating to the same collection with different query params
   * (e.g., different photo or comment) triggers a proper refresh
   */
  const navigateToActionUrl = (actionUrl: string) => {
    const currentPath = location.pathname;
    const targetUrl = new URL(actionUrl, window.location.origin);
    const targetPath = targetUrl.pathname;

    // Check if we're navigating to the same route (ignoring query params)
    if (currentPath === targetPath) {
      // Same route - use window.location.href to force full refresh
      logger.debug('Notifications', 'Force refresh for same-route navigation', {
        currentPath,
        targetPath,
        actionUrl,
      });
      window.location.href = actionUrl;
    } else {
      // Different route - use React Router navigation
      logger.debug('Notifications', 'React Router navigation', {
        currentPath,
        targetPath,
        actionUrl,
      });
      navigate(actionUrl);
    }
  };

  const handleClick = async () => {
    if (notification.status === 'unread' && !markReadLoading) {
      try {
        const result = await markRead(notification.globalId);
        if (result) {
          logger.info('Notifications', 'Marked notification as read via GraphQL', {
            globalId: notification.globalId,
          });

          // Call callback with updated notification
          onRead?.(notification);

          // Close dropdown before navigation
          onClose?.();

          // Navigate to action URL if present
          if (notification.actionUrl) {
            navigateToActionUrl(notification.actionUrl);
          }
        }
      } catch (error) {
        logger.error('Notifications', 'Failed to mark notification as read', error);
      }
    } else if (notification.actionUrl) {
      // Already read, close dropdown and navigate
      onClose?.();
      navigateToActionUrl(notification.actionUrl);
    }
  };

  const isUnread = notification.status === 'unread';

  return (
    <div
      onClick={handleClick}
      className={cn(
        'relative flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors',
        'hover:bg-surface-2',
        isUnread && 'bg-surface-1 font-semibold',
        className
      )}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      aria-label={`Notification: ${notification.title}`}
    >
      {/* Urgency Indicator */}
      <div className="flex-shrink-0 mt-1">
        <div
          className={cn('w-2 h-2 rounded-full', getUrgencyColor(notification.urgency))}
          aria-hidden="true"
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Title */}
        <div className={cn('text-sm', isUnread ? 'text-foreground' : 'text-muted')}>
          {notification.title}
        </div>

        {/* Body (if present) */}
        {notification.body && (
          <div className="text-xs text-muted mt-1 line-clamp-2">{notification.body}</div>
        )}

        {/* Timestamp */}
        <div className="text-xs text-muted mt-1">
          {formatRelativeTime(notification.createdAt)}
        </div>
      </div>

      {/* Unread Indicator */}
      {isUnread && (
        <div className="flex-shrink-0 mt-1">
          <div
            className="w-2 h-2 bg-primary rounded-full"
            aria-label="Unread"
            role="status"
          />
        </div>
      )}

      {/* Debug Overlay */}
      <DebugModeOnly>
        <div className="absolute top-1 right-1 bg-black/70 text-white text-[10px] px-2 py-1 rounded font-mono z-10">
          <div className="flex flex-col gap-0.5">
            <AdminAwareIdLink id={notification.globalId} label="Notif: " />
            {/** Examples of other debug UI we could put in...  */}
            {/* {typeof notification.payload?.photoGlobalId === 'string' && (
              <AdminAwareIdLink id={notification.payload.photoGlobalId} label="Photo: " />
            )}
            {typeof notification.payload?.critiqueGlobalId === 'string' && (
              <AdminAwareIdLink id={notification.payload.critiqueGlobalId} label="Critique: " />
            )}
            {typeof notification.payload?.replyGlobalId === 'string' && (
              <AdminAwareIdLink id={notification.payload.replyGlobalId} label="Reply: " />
            )}
            {typeof notification.payload?.collectionGlobalId === 'string' && (
              <AdminAwareIdLink id={notification.payload.collectionGlobalId} label="Collection: " />
            )} */}
          </div>
        </div>
      </DebugModeOnly>
    </div>
  );
}

export default NotificationItem;
